import React, { useState, useEffect, useCallback } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { usePermissions } from '../../hooks/usePermissions';

// --- SVG Icon Components (Unchanged) ---
const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
);

const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
    </svg>
);

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const KeyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H5v-2H3v-2H1v-4a6 6 0 016-6h4a6 6 0 016 6z" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);


const UserManagement = () => {
    const permissions = usePermissions(['Employee Management', 'hr/Employee Management/Employee Management']);
    // --- STATE AND DATA FETCHING LOGIC ---
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordUser, setPasswordUser] = useState(null);
    const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
    const [passwordError, setPasswordError] = useState('');

    // --- formData state (Unchanged) ---
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: '',
        phone: '',
        designation: '', // Renamed from position
        dept: '',        // Renamed from department
        subDept: '',
        permanentNumber: '',
        gender: '',
        dob: '',
        adhaar: '',
        pan: '',
        isActive: true
    });

    const { loading, error, makeRequest, clearError } = useAuthenticatedApi();

    const fetchUsers = useCallback(async () => {
        // ... (fetchUsers logic unchanged)
        const response = await makeRequest(async () => apiWithAuth.get('/employees'));
        if (response) {
            setUsers(response.data.data || []);
        } else {
            setUsers([]);
        }
    }, [makeRequest]);

    const fetchRoles = useCallback(async () => {
        // ... (fetchRoles logic unchanged)
        const response = await makeRequest(async () => apiWithAuth.get('/roles'));
        if (response) {
            const sortedRoles = (response.data.data || []).sort((a, b) => a.level - b.level);
            setRoles(sortedRoles);
        } else {
            setRoles([]);
        }
    }, [makeRequest]);

    useEffect(() => {
        Promise.all([fetchUsers(), fetchRoles()]);
    }, [fetchUsers, fetchRoles]);

    // --- NEW: Generalized form change handler ---
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: fieldValue
        }));
    };

    // --- resetFormData (Unchanged) ---
    const resetFormData = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            role: '',
            phone: '',
            designation: '',
            dept: '',
            subDept: '',
            permanentNumber: '',
            gender: '',
            dob: '',
            adhaar: '',
            pan: '',
            isActive: true
        });
        clearError(); // Also clear errors on reset
    }

    // --- handleSubmit (Unchanged) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        clearError();
        const url = editingUser
            ? `/employees/${editingUser._id}`
            : `/employees`;

        const method = editingUser ? 'put' : 'post';

        // Build data with new field names
        const submitData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            designation: formData.designation,
            dept: formData.dept,
            subDept: formData.subDept,
            permanentNumber: formData.permanentNumber,
            gender: formData.gender,
            dob: formData.dob || null, // Send null if empty
            adhaar: formData.adhaar,
            pan: formData.pan,
            isActive: formData.isActive
        };

        // Add password only if creating a new user and it's provided
        if (!editingUser && formData.password) {
            submitData.password = formData.password;
        }

        const response = await makeRequest(async () => apiWithAuth[method](url, submitData));

        if (response) {
            setShowForm(false);
            setEditingUser(null);
            resetFormData();
            fetchUsers();
        }
    };

    // --- handleEdit (Unchanged) ---
    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            phone: user.phone || '',
            email: user.email,
            password: '', // Password is never pre-filled
            role: user.role?._id || user.role,
            designation: user.designation || '', // Renamed from position
            dept: user.dept || '',               // Renamed from department
            subDept: user.subDept || '',
            permanentNumber: user.permanentNumber || '',
            gender: user.gender || '',
            dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : '', // Format for <input type="date">
            adhaar: user.adhaar || '',
            pan: user.pan || '',
            isActive: user.isActive
        });
        setShowForm(true);
    };

    // --- handleDelete (Unchanged) ---
    const handleDelete = async (userId) => {
        // Per your instruction, using a custom modal/confirm is better,
        // but for this example, window.confirm is used.
        // A real implementation should use a state-controlled modal.
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            const response = await makeRequest(async () => apiWithAuth.delete(`/employees/${userId}`));
            if (response) {
                fetchUsers();
            }
        }
    };

    // --- Password Handlers (Unchanged) ---
    const handleResetPassword = (user) => {
        setPasswordUser(user);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setPasswordError('');
        setShowPasswordForm(true);
    };

    const handlePasswordChange = (e) => {
        setPasswordData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        if (passwordError) setPasswordError('');
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters long');
            return;
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        const response = await makeRequest(async () => apiWithAuth.put(
            `/employees/${passwordUser._id}`,
            { password: passwordData.newPassword, isPasswordReset: true }
        ));

        if (response) {
            setShowPasswordForm(false);
            setPasswordUser(null);
        }
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingUser(null);
        resetFormData();
        clearError();
    };

    const handleCancelPassword = () => {
        setShowPasswordForm(false);
        setPasswordUser(null);
        setPasswordError('');
    };

    // --- UPDATED: Filter logic with employeeId ---
    const filteredUsers = users.filter(user => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`;
        const searchTerm = search.toLowerCase();
        return [
            fullName,
            user.email,
            user.employeeId,  // Added
            user.role?.name,
            user.dept,
            user.designation,
            user.subDept
        ].some(field =>
            String(field || '').toLowerCase().includes(searchTerm)
        );
    });

    // --- JSX ---
    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header (Unchanged) */}
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 text-center">User Management</h1>
                        <p className="mt-1 text-gray-600 text-center">Create, edit, and manage system users.</p>
                    </div>
                    <button
                        className="mt-4 sm:mt-0 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        onClick={() => setShowForm(true)}
                    >
                        <PlusIcon />
                        Add New User
                    </button>
                </header>

                {/* Error (Unchanged) */}
                {error && !showForm && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                        <p className="font-bold">Error</p>
                        <p>{error}</p>
                    </div>
                )}

                {/* --- UPDATED: User Table --- */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <SearchIcon />
                            </div>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, email, ID, role, dept, designation..."
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="bg-gray-50 text-xs text-gray-700 uppercase">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Name</th>
                                    <th scope="col" className="px-6 py-3">Employee ID</th>
                                    <th scope="col" className="px-6 py-3">Designation & Dept</th>
                                    <th scope="col" className="px-6 py-3">Role</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    <th scope="col" className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-12 text-gray-500">Loading users...</td></tr>
                                ) : filteredUsers.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-12 text-gray-500">No users found.</td></tr>
                                ) : (
                                    filteredUsers.map((user, index) => (
                                        <tr key={user._id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50`}>
                                            <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                                                <div className="font-bold">{(user.firstName || '') + ' ' + (user.lastName || '')}</div>
                                                <div className="text-xs text-gray-500">{user.email}</div>
                                            </td>
                                            <td className="px-6 py-4 font-mono text-gray-700">{user.employeeId}</td>
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-gray-800">{user.designation || 'N/A'}</div>
                                                <div className="text-xs text-gray-500">
                                                    {user.dept}
                                                    {user.subDept && ` (${user.subDept})`}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">{user.role?.name || 'N/A'}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {user.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {/* Actions (Unchanged) */}
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button onClick={() => handleEdit(user)} className="p-2 text-gray-400 hover:text-indigo-600 group relative">
                                                        <EditIcon /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Edit</span>
                                                    </button>
                                                    <button onClick={() => handleResetPassword(user)} className="p-2 text-gray-400 hover:text-yellow-600 group relative">
                                                        <KeyIcon /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Reset Password</span>
                                                    </button>
                                                    <button onClick={() => handleDelete(user._id)} className="p-2 text-gray-400 hover:text-red-600 group relative">
                                                        <DeleteIcon /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-700 text-white text-xs rounded py-1 px-2">Delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* --- UPDATED: Add/Edit User Modal with validation cues --- */}
            <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ${showForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
            <div className={`fixed inset-0 z-50 flex justify-center items-center p-4 transition-all duration-300 ${showForm ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"> {/* Increased max-w-2xl */}
                    <header className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">{editingUser ? 'Edit User' : 'Create New User'}</h2>
                        <button onClick={handleCancelForm} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><CloseIcon /></button>
                    </header>
                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-6 overflow-y-auto space-y-6">
                            {error && (
                                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                                    <p>{error}</p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                                    <input type="text" name="firstName" value={formData.firstName} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                                    <input type="text" name="lastName" value={formData.lastName} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                                    <input type="email" name="email" value={formData.email} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                                    <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                                </div>
                            </div>
                            {!editingUser && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                                    <input type="password" name="password" value={formData.password} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required={!editingUser} minLength="6" placeholder="Min 6 characters" />
                                </div>
                            )}

                            <hr className="my-2 border-t border-gray-200" />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                                    <input type="text" name="designation" value={formData.designation} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., Manager" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                                    <select name="role" value={formData.role} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required>
                                        <option value="">Select Role</option>
                                        {roles.map(role => (<option key={role._id} value={role._id}>{role.name} (Level: {role.level})</option>))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Dept</label>
                                    <input type="text" name="dept" value={formData.dept} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., IT, HR" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Dept</label>
                                    <input type="text" name="subDept" value={formData.subDept} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g., Backend, Recruitment" />
                                </div>
                            </div>

                            <hr className="my-2 border-t border-gray-200" />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                    <input type="date" name="dob" value={formData.dob} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                                    <select name="gender" value={formData.gender} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required>
                                        <option value="">Select Gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                        <option value="Prefer not to say">Prefer not to say</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Permanent Number</label>
                                    <input type="text" name="permanentNumber" value={formData.permanentNumber} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                                    <input type="text" name="pan" value={formData.pan} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Number</label>
                                <input type="text" name="adhaar" value={formData.adhaar} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>

                            <div>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleFormChange} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                                    <span className="font-medium text-gray-700">Active User</span>
                                </label>
                            </div>
                        </div>
                        <footer className="flex gap-3 justify-end p-4 bg-gray-50 border-t mt-auto">
                            <button type="button" onClick={handleCancelForm} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">{editingUser ? 'Update User' : 'Create User'}</button>
                        </footer>
                    </form>
                </div>
            </div>

            {/* Password Reset Modal (Unchanged) */}
            <div className={`fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300 ${showPasswordForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}></div>
            <div className={`fixed inset-0 z-50 flex justify-center items-center p-4 transition-all duration-300 ${showPasswordForm ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
                    <header className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">Reset Password</h2>
                        <button onClick={handleCancelPassword} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><CloseIcon /></button>
                    </header>
                    <form onSubmit={handlePasswordSubmit} className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-gray-600">Resetting password for <span className="font-bold">{passwordUser?.firstName} {passwordUser?.lastName}</span>.</p>
                            {passwordError && (
                                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
                                    {passwordError}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                                <input type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required minLength="6" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                <input type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required minLength="6" />
                            </div>
                        </div>
                        <footer className="flex gap-3 justify-end p-4 bg-gray-50 border-t">
                            <button type="button" onClick={handleCancelPassword} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">Set New Password</button>
                        </footer>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;